import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  CircularProgress,
  Grid,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

const TimeSlotAvailabilityPage = () => {
  const { t } = useTranslation();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [cp, setCp] = useState('');
  const [loading, setLoading] = useState(false);
  const [availabilityData, setAvailabilityData] = useState([]);
  const [error, setError] = useState(null);

  const getAvailabilityColor = (count) => {
    if (count >= 5) return 'success';
    if (count >= 3) return 'warning';
    return 'error';
  };

  const fetchAvailability = async () => {
    if (!cp) {
      setError(t('timeslotAvailability.enterPostalCode'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `https://kortex.phone.gs/vtigercrm/moncrm/agendaEspagne/api/dgcheckdispo.php?cp=${cp}&quiet=1&date=${date}`
      );
      setAvailabilityData(response.data);
    } catch (err) {
      setError(t('timeslotAvailability.fetchError'));
      console.error('Error fetching availability:', err);
    } finally {
      setLoading(false);
    }
  };

  // Grouper les données par date et heure
  const groupedData = availabilityData.reduce((acc, item) => {
    const key = `${item.date}-${item.time}`;
    if (!acc[key]) {
      acc[key] = {
        date: item.date,
        time: item.time,
        teams: []
      };
    }
    acc[key].teams.push(item.team);
    return acc;
  }, {});

  // Trier les dates
  const sortedDates = Object.values(groupedData)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .reduce((acc, item) => {
      if (!acc[item.date]) {
        acc[item.date] = [];
      }
      acc[item.date].push(item);
      return acc;
    }, {});

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('timeslotAvailability.title')}
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label={t('timeslotAvailability.date')}
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label={t('timeslotAvailability.postalCode')}
              value={cp}
              onChange={(e) => setCp(e.target.value)}
              error={!!error}
              helperText={error}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Button
              variant="contained"
              onClick={fetchAvailability}
              disabled={loading}
              sx={{ mr: 2 }}
            >
              {t('timeslotAvailability.checkAvailability')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setDate(new Date().toISOString().split('T')[0]);
                setCp('');
                setAvailabilityData([]);
                setError(null);
              }}
            >
              {t('timeslotAvailability.reset')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>{t('timeslotAvailability.loading')}</Typography>
        </Box>
      )}

      {availabilityData.length === 0 && !loading && (
        <Typography variant="body1" sx={{ textAlign: 'center', my: 4 }}>
          {t('timeslotAvailability.noData')}
        </Typography>
      )}

      {availabilityData.length > 0 && (
        <Paper sx={{ p: 2 }}>
          {Object.entries(sortedDates).map(([date, timeSlots]) => (
            <Box key={date} sx={{ mb: 4 }}>
              <Typography variant="h6" gutterBottom>
                {new Date(date).toLocaleDateString('fr-FR', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Typography>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('timeslotAvailability.time')}</TableCell>
                    <TableCell>{t('timeslotAvailability.availableTeams')}</TableCell>
                    <TableCell>Places disponibles</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeSlots.map((slot) => (
                    <TableRow key={`${date}-${slot.time}`}>
                      <TableCell>{slot.time}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {slot.teams.sort((a, b) => {
                            const numA = parseInt(a.replace('EQUIPE ', ''));
                            const numB = parseInt(b.replace('EQUIPE ', ''));
                            return numA - numB;
                          }).map((team) => (
                            <Paper
                              key={team}
                              sx={{
                                p: 1,
                                backgroundColor: 'primary.main',
                                color: 'white',
                                borderRadius: 1
                              }}
                            >
                              {team}
                            </Paper>
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${slot.teams.length} places`}
                          color={getAvailabilityColor(slot.teams.length)}
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default TimeSlotAvailabilityPage;
